import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { handleApiError, showError, showSuccess } from '../../utils/handleApiError';
import { API_BASE_URL } from '../../services/api';
import ConfirmModal from '../ConfirmModal';

interface Language {
  id: number;
  code: string;
  name: string;
  native_name: string;
  is_active: boolean;
  is_default: boolean;
}

const LanguageManagement: React.FC = () => {
  const { t } = useLanguage();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState<Language | null>(null);
  const [deleteLanguageId, setDeleteLanguageId] = useState<number | null>(null);
  const [importData, setImportData] = useState<{ languageCode: string; file: File } | null>(null);
  const [newLanguage, setNewLanguage] = useState({
    code: '',
    name: '',
    native_name: '',
    is_active: true
  });

  useEffect(() => {
    loadLanguages();
  }, []);

  const loadLanguages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/translation-management/languages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLanguages(data.languages);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
      handleApiError(error, 'Hiba történt a nyelvek betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLanguage = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/admin/translation-management/languages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newLanguage)
      });

      if (response.ok) {
        showSuccess('Nyelv sikeresen létrehozva');
        setShowForm(false);
        setNewLanguage({ code: '', name: '', native_name: '', is_active: true });
        loadLanguages();
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Nem sikerült létrehozni a nyelvet');
      }
    } catch (error) {
      console.error('Error creating language:', error);
      handleApiError(error, 'Hiba történt a nyelv létrehozásakor');
    }
  };

  const handleUpdateLanguage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLanguage) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/translation-management/languages/${editingLanguage.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: editingLanguage.code,
          name: editingLanguage.name,
          native_name: editingLanguage.native_name,
          is_active: editingLanguage.is_active
        })
      });

      if (response.ok) {
        showSuccess('Nyelv sikeresen frissítve');
        setEditingLanguage(null);
        setShowForm(false);
        loadLanguages();
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Nem sikerült frissíteni a nyelvet');
      }
    } catch (error) {
      console.error('Error updating language:', error);
      handleApiError(error, 'Hiba történt a nyelv frissítésekor');
    }
  };

  const handleDeleteLanguage = async (languageId: number, isDefault: boolean) => {
    if (isDefault) {
      showError('Az alapértelmezett nyelvet nem lehet törölni!');
      return;
    }
    setDeleteLanguageId(languageId);
  };

  const doDeleteLanguage = async () => {
    if (deleteLanguageId === null) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/translation-management/languages/${deleteLanguageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        loadLanguages();
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Nem sikerült törölni a nyelvet');
      }
    } catch (error) {
      console.error('Error deleting language:', error);
      handleApiError(error, 'Hiba történt a nyelv törlésekor');
    } finally {
      setDeleteLanguageId(null);
    }
  };

  const exportTranslations = async (languageCode: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/translation-management/export/${languageCode}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data.translations, null, 2)], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `translations-${languageCode}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting translations:', error);
      handleApiError(error, 'Hiba történt a fordítások exportálásakor');
    }
  };

  const handleImportTranslations = async (languageCode: string, file: File, overwrite: boolean = false) => {
    try {
      const text = await file.text();
      const translations = JSON.parse(text);

      const response = await fetch(`${API_BASE_URL}/admin/translation-management/import/${languageCode}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          translations,
          overwrite
        })
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(`Importálás sikeres! Importált: ${result.imported}, Kihagyott: ${result.skipped}`);
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Nem sikerült importálni a fordításokat');
      }
    } catch (error) {
      console.error('Error importing translations:', error);
      handleApiError(error, 'Hiba történt a fordítások importálásakor');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">{t('language_management', 'admin')}</h2>
        <button
          onClick={() => {
            setEditingLanguage(null);
            setNewLanguage({ code: '', name: '', native_name: '', is_active: true });
            setShowForm(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
        >
          + {t('add_language', 'admin')}
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('language_code', 'admin')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('name', 'form')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('native_name', 'admin')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('status', 'form')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('actions', 'action')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {languages.map((language) => (
              <tr key={language.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900">{language.code}</span>
                    {language.is_default && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Alapértelmezett
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {language.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {language.native_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    language.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {language.is_active ? 'Aktív' : 'Inaktív'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingLanguage(language);
                        setShowForm(true);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      {t('edit', 'action')}
                    </button>
                    <button
                      onClick={() => exportTranslations(language.code)}
                      className="text-green-600 hover:text-green-900"
                    >
                      Export
                    </button>
                    <label className="text-purple-600 hover:text-purple-900 cursor-pointer">
                      Import
                      <input
                        type="file"
                        accept=".json"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setImportData({ languageCode: language.code, file });
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {!language.is_default && (
                      <button
                        onClick={() => handleDeleteLanguage(language.id, language.is_default)}
                        className="text-red-600 hover:text-red-900"
                      >
                        {t('delete', 'action')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Language Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingLanguage ? t('edit_language', 'admin') : t('add_language', 'admin')}
            </h3>
            <form onSubmit={editingLanguage ? handleUpdateLanguage : handleCreateLanguage} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('language_code', 'admin')} *</label>
                <input
                  type="text"
                  required
                  maxLength={2}
                  placeholder="pl. en, de, fr"
                  value={editingLanguage ? editingLanguage.code : newLanguage.code}
                  onChange={(e) => editingLanguage 
                    ? setEditingLanguage({ ...editingLanguage, code: e.target.value.toLowerCase() })
                    : setNewLanguage({ ...newLanguage, code: e.target.value.toLowerCase() })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
                <p className="mt-1 text-xs text-gray-500">Kétjegyű ISO nyelvi kód (pl. en, de, fr)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('name', 'form')} *</label>
                <input
                  type="text"
                  required
                  placeholder="pl. English, Deutsch, Français"
                  value={editingLanguage ? editingLanguage.name : newLanguage.name}
                  onChange={(e) => editingLanguage 
                    ? setEditingLanguage({ ...editingLanguage, name: e.target.value })
                    : setNewLanguage({ ...newLanguage, name: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('native_name', 'admin')} *</label>
                <input
                  type="text"
                  required
                  placeholder="pl. English, Deutsch, Français"
                  value={editingLanguage ? editingLanguage.native_name : newLanguage.native_name}
                  onChange={(e) => editingLanguage 
                    ? setEditingLanguage({ ...editingLanguage, native_name: e.target.value })
                    : setNewLanguage({ ...newLanguage, native_name: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
                <p className="mt-1 text-xs text-gray-500">A nyelv natív neve (ahogy az anyanyelvi beszélők írják)</p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingLanguage ? editingLanguage.is_active : newLanguage.is_active}
                  onChange={(e) => editingLanguage 
                    ? setEditingLanguage({ ...editingLanguage, is_active: e.target.checked })
                    : setNewLanguage({ ...newLanguage, is_active: e.target.checked })
                  }
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">{t('is_active', 'admin')}</label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingLanguage(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('save', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={deleteLanguageId !== null}
        onConfirm={doDeleteLanguage}
        onCancel={() => setDeleteLanguageId(null)}
        title="Nyelv törlése"
        message="Biztosan törölni szeretnéd ezt a nyelvet? Ez törli az összes hozzá tartozó fordítást is!"
        confirmLabel="Törlés"
      />

      <ConfirmModal
        isOpen={importData !== null}
        onConfirm={async () => {
          if (importData) {
            await handleImportTranslations(importData.languageCode, importData.file, true);
          }
          setImportData(null);
        }}
        onCancel={() => {
          if (importData) {
            handleImportTranslations(importData.languageCode, importData.file, false);
          }
          setImportData(null);
        }}
        title="Fordítások importálása"
        message="Felülírja a meglévő fordításokat?"
        confirmLabel="Felülírás"
        cancelLabel="Megtartás"
        variant="warning"
      />
    </div>
  );
};

export default LanguageManagement;